import { useEffect, useMemo, useState } from 'react';
import { RiRefundLine } from 'react-icons/ri';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import {
  Badge,
  Box,
  Button,
  Center,
  Heading,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { getDebitTxnsScreeningDetails } from 'api/transactions';
import * as ROUTES from 'constants/routes';
import { TRANSACTION } from 'constants/transactionDetails';
import {
  DEBIT_TRANSACTION_STATUSES,
  TRANSACTION_SCREENING_TABLE_HEADINGS,
} from 'modules/debit-transactions/constants/constants';
import RefundDebitTxnModal from 'modules/merchantinfo/components/RefundDebitTxnModal';
import { formatTransactionStatus, getTransactionStatusBadgeColor } from 'modules/merchantinfo/utils/utils';
import { formatDateTime } from 'utils/utils';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';
import Search from 'components/SearchBox/Search';

export default function DebitTransactionTable({ title = '', status }) {
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const history = useHistory();

  const isRfiRequestedTab = status === DEBIT_TRANSACTION_STATUSES.RFI_REQUESTED;

  const handleOpenRefundModal = (e, transaction) => {
    e.stopPropagation();
    setSelectedTransaction({
      id: transaction?.transaction_id,
      title: transaction?.transfer_method,
      amount: transaction?.amount,
      currency_code: transaction?.currency,
      status: status,
    });
    setIsRefundModalOpen(true);
  };

  const filteredTransactionDetails = useMemo(() => {
    if (!searchTerm) return transactionDetails?.transactions;

    return transactionDetails?.transactions?.filter((transaction) => {
      return (
        transaction?.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction?.partner_code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [transactionDetails, searchTerm]);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const response = await getDebitTxnsScreeningDetails(status);

      setTransactionDetails(response?.data?.data);
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleRowClick = (entityId) => {
    if (status === DEBIT_TRANSACTION_STATUSES.RFI_REQUESTED) {
      const route = `${ROUTES.KYC_RFI_ENTITY_DETAILS_ROUTE}${entityId}`;

      history.push(route, { entityId });
    }
  };

  return (
    <Box overflowX='auto' maxWidth='100%' p={4} borderRadius='lg' boxShadow='sm' bg='white'>
      <Heading as='h1' size='md' mb={3} mt={8}>
        {title}{' '}
        <Badge ml={1} colorScheme='blue' variant='solid' px={2}>
          {transactionDetails?.count}
        </Badge>
      </Heading>

      {!!transactionDetails?.count && <Search searchType={TRANSACTION} setSearchTerm={setSearchTerm} />}

      <LoadingErrorWrapper
        isLoading={isLoading}
        errorTitle='Error fetching transaction screening details.'
        error={error}
      >
        <TableContainer borderRadius='lg' border='1px solid' borderColor='gray.200' overflowX='auto' mt={4}>
          <Table variant='simple' size='md'>
            <Thead bg='gray.100' position='sticky' top={0} zIndex={1}>
              <Tr whiteSpace='nowrap'>
                {TRANSACTION_SCREENING_TABLE_HEADINGS.map((heading, idx) => (
                  <Th key={idx} fontSize='sm' fontWeight='bold' color='gray.700' py={3}>
                    {heading}
                  </Th>
                ))}
                <Th fontSize='sm' fontWeight='bold' color='gray.700' py={3}>
                  Whitelist Info
                </Th>
                {isRfiRequestedTab && (
                  <Th fontSize='sm' fontWeight='bold' color='gray.700' py={3}>
                    Actions
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {filteredTransactionDetails?.map((transaction, idx) => (
                <Tr
                  key={idx}
                  fontSize='sm'
                  cursor='pointer'
                  _hover={{ bg: 'gray.50' }}
                  onClick={() => handleRowClick(transaction?.entity_id)}
                >
                  <Td whiteSpace='nowrap' color='gray.600' fontWeight={'bold'}>
                    {transaction?.transaction_id ?? '-'}
                  </Td>
                  <Td whiteSpace='nowrap' color='gray.600'>
                    {formatDateTime(transaction?.date)}
                  </Td>
                  <Td whiteSpace='nowrap' color='gray.600'>
                    {transaction?.transfer_method ?? '-'}
                  </Td>
                  <Td whiteSpace='nowrap' color='gray.600' fontWeight={'bold'}>
                    {transaction?.partner_code ?? '-'}
                  </Td>
                  <Td whiteSpace='nowrap' color='gray.600'>
                    {transaction?.currency}
                  </Td>
                  <Td whiteSpace='nowrap' color='gray.600'>
                    {transaction?.amount}
                  </Td>
                  <Td whiteSpace='nowrap' color='gray.600'>
                    <Badge colorScheme={getTransactionStatusBadgeColor(transaction?.kyt_status)} rounded='0.8em' px={3}>
                      {formatTransactionStatus(transaction?.kyt_status)}
                    </Badge>
                  </Td>
                  <Td whiteSpace='nowrap' color='gray.600'>
                    <Badge colorScheme={getTransactionStatusBadgeColor(transaction?.kyc_status)} rounded='0.8em' px={3}>
                      {formatTransactionStatus(transaction?.kyc_status)}
                    </Badge>
                  </Td>
                  <Td whiteSpace='nowrap' color='gray.600'>
                    <Badge
                      colorScheme={getTransactionStatusBadgeColor(transaction?.travel_rule_status)}
                      rounded='0.8em'
                      px={3}
                    >
                      {formatTransactionStatus(transaction?.travel_rule_status)}
                    </Badge>
                  </Td>
                  <Td whiteSpace='nowrap' color='gray.600'>
                    <Badge
                      colorScheme={getTransactionStatusBadgeColor(transaction?.chainalysis_status)}
                      rounded='0.8em'
                      px={3}
                    >
                      {formatTransactionStatus(transaction?.chainalysis_status)}
                    </Badge>
                  </Td>
                  <Td whiteSpace='nowrap' color='gray.600'>
                    <VStack>
                      <Text>{transaction?.whitelist_info?.whitelist_status}</Text>
                      <Text>{transaction?.whitelist_info?.whitelist_document_identifier}</Text>
                    </VStack>
                  </Td>
                  {isRfiRequestedTab && (
                    <Td whiteSpace='nowrap'>
                      <Button
                        size='sm'
                        variant='link'
                        width='fit-content'
                        onClick={(e) => handleOpenRefundModal(e, transaction)}
                        leftIcon={<RiRefundLine size='15px' />}
                      >
                        Refund
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        {!transactionDetails?.count && (
          <Center>
            <Text color='gray' mt={4}>
              No transaction found.
            </Text>
          </Center>
        )}
      </LoadingErrorWrapper>

      {isRefundModalOpen && selectedTransaction && (
        <RefundDebitTxnModal
          transaction={selectedTransaction}
          isOpen={isRefundModalOpen}
          onClose={() => setIsRefundModalOpen(false)}
          fetchTransactions={fetchTransactions}
        />
      )}
    </Box>
  );
}
